/**
 * Calling screen — the full-screen modal shown while an anonymous call is in
 * progress. Replaces `calling-screen.js`.
 *
 * Coordinates: dial-tone audio, status tracker, and the user-cancel button.
 */

import { createLogger } from '../../core/logger.js';
import { escape } from '../../shared/escape.js';
import { format as formatPlate, normalize as normalizePlate } from '../../shared/plate.js';
import * as alert from '../../core/alert.js';
import * as callService from './call.service.js';
import * as tracker from './callTracker.js';
import * as dialTone from './dialingTone.js';
import * as sendView from '../messaging/send.view.js';
import * as phantomStore from '../phantom/phantom.store.js';
import * as phantomConfig from '../phantom/phantom.config.js';

const log = createLogger('calling.view');

const OVERLAY_ID = 'callingScreenOverlay';
const AUTO_CANCEL_MS = 35_000;

let stylesInjected = false;
/** @type {ReturnType<typeof setTimeout> | null} */
let autoCancelTimer = null;
/** @type {(() => void) | null} */
let activeTrackerStop = null;
/** Plate the call is currently against — needed by the action buttons. */
let currentPlate = '';
/** Track which UI state the screen is in. Drives icon color + button states. */
let isActive = false;

/**
 * Confirm + initiate. Shows the confirmation dialog first, then opens the
 * calling screen on confirm.
 *
 * @param {string} targetPlate
 */
export async function startWithConfirmation(targetPlate) {
  const plate = normalizePlate(targetPlate);
  if (!plate) {
    void alert.showAlert({ title: 'שגיאה', message: 'מספר רכב לא תקין.' });
    return;
  }
  const ok = await alert.showConfirm({
    title: 'שיחה אנונימית',
    message: `להתקשר לבעל הרכב ${formatPlate(plate)}?\nהמספר שלך לא ייחשף.`,
    confirm: 'התקשר',
    cancel: 'ביטול',
  });
  if (!ok) return;
  // Phantom branch: the plate isn't registered to anyone. Simulate the
  // dial-tone screen for 30 seconds and finish with a "no answer" toast,
  // without touching Twilio or the call tracker. Keeps the user engaged
  // while the database is still growing.
  if (phantomConfig.isEnabled() && phantomStore.isPhantom(plate)) {
    await startPhantomCall(plate);
    return;
  }
  await start(plate);
}

/**
 * Open the calling screen and initiate the call. Skips confirmation.
 * @param {string} targetPlate
 */
export async function start(targetPlate) {
  const plate = normalizePlate(targetPlate);
  if (!plate) return;
  showScreen(plate);
  setStatusText('מתחיל שיחה...');
  dialTone.start();

  const result = await callService.initiate(plate);
  if (!result.ok || !result.callSid) {
    dialTone.stop();
    hideScreen();
    void alert.showAlert({
      title: 'שגיאה',
      message: result.message ?? 'לא ניתן ליצור את השיחה.',
    });
    return;
  }

  activeTrackerStop = tracker.track(result.callSid, ({ status }) => {
    log.info('call status:', status);
    setStatusText(statusLabel(status));
    if (status === 'in-progress' || status === 'answered') {
      dialTone.stop();
      setActiveState(true);
    }
    if (['completed', 'no-answer', 'busy', 'failed', 'canceled'].includes(status)) {
      finish(statusFinalText(status));
    }
  });

  // Auto-cancel safety
  if (autoCancelTimer) clearTimeout(autoCancelTimer);
  autoCancelTimer = setTimeout(() => {
    log.warn('auto-cancel timeout reached');
    void cancel();
  }, AUTO_CANCEL_MS);
}

/** User-cancel. */
export async function cancel() {
  // Phantom mode has no Twilio call to cancel — just close the screen.
  if (phantomCallTimer !== null) {
    clearTimeout(phantomCallTimer);
    phantomCallTimer = null;
    dialTone.stop();
    hideScreen();
    return;
  }
  await callService.cancel();
  finish('השיחה בוטלה');
}

/**
 * Phantom call: same UI as a real call, but no Twilio. Plays the dial tone
 * for 30 seconds, then shows a "no answer" toast and closes. The user
 * believes they were calling a real driver who didn't pick up.
 *
 * @param {string} plate
 */
/** @type {ReturnType<typeof setTimeout> | null} */
let phantomCallTimer = null;
const PHANTOM_CALL_DURATION_MS = 30_000;

async function startPhantomCall(plate) {
  showScreen(plate);
  setStatusText('מצלצל...');
  dialTone.start();

  // Single 30s timer — no tracker subscription, no Twilio call.
  phantomCallTimer = setTimeout(() => {
    phantomCallTimer = null;
    dialTone.stop();
    hideScreen();
    alert.showToast('הנהג לא ענה לשיחה. ניתן לנסות שוב בעוד זמן קצר.', { kind: 'info' });
  }, PHANTOM_CALL_DURATION_MS);
}

// ====================================================================
// internals
// ====================================================================

function ensureOverlay() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'calling-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <div class="calling-card" role="dialog" aria-modal="true">
      <div class="calling-header">
        <h2 class="calling-plate" dir="ltr"></h2>
        <p class="calling-status">מתחיל שיחה...</p>
      </div>

      <div class="calling-visual">
        <div class="calling-rings" aria-hidden="true">
          <span class="calling-ring"></span>
          <span class="calling-ring"></span>
          <span class="calling-ring"></span>
        </div>
        <div class="calling-icon" aria-hidden="true">📞</div>
      </div>

      <div class="calling-actions">
        <button class="cv-action" type="button" data-action="speaker" aria-pressed="false" aria-label="רמקול">
          <span class="cv-action-circle">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4v8a4.5 4.5 0 002.5-4zM14 3.23v2.06a7 7 0 010 13.42v2.06a9 9 0 000-17.54z"/>
            </svg>
          </span>
          <span class="cv-action-label">רמקול</span>
        </button>
        <button class="cv-action" type="button" data-action="message" aria-label="שלח הודעה">
          <span class="cv-action-circle">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
            </svg>
          </span>
          <span class="cv-action-label">הודעה</span>
        </button>
        <button class="cv-action" type="button" data-action="results" aria-label="חזרה לתוצאות החיפוש">
          <span class="cv-action-circle">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-5h14v5z"/>
              <circle cx="7.5" cy="14.5" r="1.5"/>
              <circle cx="16.5" cy="14.5" r="1.5"/>
            </svg>
          </span>
          <span class="cv-action-label">תוצאות חיפוש</span>
        </button>
      </div>

      <div class="calling-end-wrap">
        <button class="calling-end" type="button" aria-label="סיים שיחה">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  injectStyles();
  wireActions(overlay);
  return overlay;
}

/**
 * Wire the action buttons + end button. Idempotent thanks to `ensureOverlay`
 * which only creates the overlay once.
 *
 * @param {HTMLElement} overlay
 */
function wireActions(overlay) {
  overlay.querySelector('.calling-end')?.addEventListener('click', () => { void cancel(); });

  overlay.querySelector('[data-action="speaker"]')?.addEventListener('click', (e) => {
    const btn = /** @type {HTMLElement} */ (e.currentTarget);
    if (isActive) return; // disabled in active state (see setActiveState)
    const next = !dialTone.isBoosted();
    dialTone.setBoost(next);
    btn.classList.toggle('is-active', next);
    btn.setAttribute('aria-pressed', next ? 'true' : 'false');
  });

  overlay.querySelector('[data-action="message"]')?.addEventListener('click', () => {
    if (!currentPlate) return;
    // Send-message modal opens above the call screen — bump its z-index so
    // it lands on top of our overlay (which sits at 90000). The call keeps
    // ringing in the background until the user returns or ends it.
    const msgModal = document.getElementById('sendMessageModal');
    if (msgModal) msgModal.style.zIndex = '100000';
    try { sendView.open(currentPlate); }
    catch (err) { log.warn('open message modal failed:', err); }
  });

  overlay.querySelector('[data-action="results"]')?.addEventListener('click', () => {
    // Same approach as message: open the existing results modal above the
    // call screen rather than closing the call. The results modal was
    // populated by the search flow that led to this call.
    const resModal = document.getElementById('searchResultsModal');
    if (!resModal) {
      log.warn('searchResultsModal not in DOM');
      return;
    }
    resModal.style.zIndex = '100000';
    const w = /** @type {any} */ (window);
    if (w.searchResultsModal && typeof w.searchResultsModal.open === 'function') {
      try { w.searchResultsModal.open(); return; }
      catch (err) { log.warn('searchResultsModal.open failed:', err); }
    }
    // Fallback: open manually if the legacy controller didn't expose its API.
    resModal.style.display = 'flex';
    resModal.classList.add('open');
    resModal.setAttribute('aria-hidden', 'false');
  });
}

/**
 * Switch the screen between "ringing" and "in-progress" looks. Disables the
 * speaker button when active (browser can't control OS-level call audio).
 *
 * @param {boolean} on
 */
function setActiveState(on) {
  isActive = on;
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.toggle('is-active', on);
  const speaker = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('[data-action="speaker"]'));
  if (speaker) {
    speaker.disabled = on;
    speaker.title = on ? 'הרמקול נשלט על־ידי מערכת ההפעלה' : '';
    if (on) {
      speaker.classList.remove('is-active');
      speaker.setAttribute('aria-pressed', 'false');
    }
  }
}

/** @param {string} plate */
function showScreen(plate) {
  currentPlate = plate;
  isActive = false;
  const overlay = ensureOverlay();
  overlay.classList.remove('is-active');
  /** @type {HTMLElement} */ (overlay.querySelector('.calling-plate')).textContent =
    formatPlate(plate);
  // Reset speaker visual to OFF on every new call (the underlying
  // dialingTone state defaults to non-boost when start() runs).
  const speaker = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('[data-action="speaker"]'));
  if (speaker) {
    speaker.disabled = false;
    speaker.classList.remove('is-active');
    speaker.setAttribute('aria-pressed', 'false');
    speaker.title = '';
  }
  overlay.style.display = 'flex';
  overlay.setAttribute('aria-hidden', 'false');
  requestAnimationFrame(() => overlay.classList.add('open'));
}

function hideScreen() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
  overlay.setAttribute('aria-hidden', 'true');
  setTimeout(() => {
    if (overlay.getAttribute('aria-hidden') === 'true') overlay.style.display = 'none';
  }, 200);
}

/** @param {string} text */
function setStatusText(text) {
  const overlay = document.getElementById(OVERLAY_ID);
  const el = overlay?.querySelector('.calling-status');
  if (el) el.textContent = text;
}

/** @param {string} [reasonText] */
function finish(reasonText) {
  if (autoCancelTimer) { clearTimeout(autoCancelTimer); autoCancelTimer = null; }
  if (activeTrackerStop) { activeTrackerStop(); activeTrackerStop = null; }
  dialTone.stop();
  // Reset boost so the next call starts from the comfortable default.
  dialTone.setBoost(false);
  callService.markEnded();
  if (reasonText) setStatusText(reasonText);
  setTimeout(hideScreen, 1200);
}

/** @param {string} status */
function statusLabel(status) {
  switch (status) {
    case 'initiated': return 'מתחיל שיחה...';
    case 'queued': return 'בתור...';
    case 'ringing': return 'מצלצל...';
    case 'in-progress':
    case 'answered': return 'בשיחה';
    case 'completed': return 'השיחה הסתיימה';
    case 'no-answer': return 'אין מענה';
    case 'busy': return 'הקו תפוס';
    case 'failed': return 'השיחה נכשלה';
    case 'canceled': return 'השיחה בוטלה';
    default: return status;
  }
}

/** @param {string} status */
function statusFinalText(status) {
  return statusLabel(status);
}

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .calling-overlay {
      position: fixed; inset: 0; z-index: 90000;
      background: linear-gradient(180deg, #2a3942 0%, #1f2a30 100%);
      color: #fff;
      display: none; align-items: center; justify-content: center;
      opacity: 0; transition: opacity .2s;
    }
    .calling-overlay.open { opacity: 1; }
    .calling-card {
      direction: rtl; text-align: center;
      width: min(420px, 100%);
      height: 100%;
      /* Bottom padding leaves room for the native AdMob banner (which is
         layered ABOVE the WebView and would otherwise cover the end-call
         button). \`--ad-banner-h\` is set by admob.service when the banner
         loads and updates live via the \`bannerAdSizeChanged\` event; falls
         back to 0px in browser / no-ad mode. */
      padding: 48px 28px calc(36px + var(--ad-banner-h, 0px));
      display: flex; flex-direction: column;
      box-sizing: border-box;
    }
    /* Header */
    .calling-header { flex-shrink: 0; }
    .calling-plate {
      font-size: 30px; font-weight: 700; letter-spacing: 1px;
      margin: 0; color: #fff;
    }
    .calling-status {
      margin: 10px 0 0; color: #b8c5cd; font-size: 16px;
      min-height: 22px;
    }
    /* Visual area: rings + icon, centered vertically */
    .calling-visual {
      flex: 1; position: relative;
      display: flex; align-items: center; justify-content: center;
      min-height: 130px;
    }
    .calling-rings { position: absolute; inset: 0; pointer-events: none; }
    .calling-ring {
      position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 90px; height: 90px;
      border: 2px solid rgba(99, 179, 237, 0.55);
      border-radius: 50%;
      animation: calling-pulse 1.8s infinite ease-out;
    }
    .calling-ring:nth-child(2) { animation-delay: .6s; }
    .calling-ring:nth-child(3) { animation-delay: 1.2s; }
    /* In-progress state: rings hide, icon glows green */
    .calling-overlay.is-active .calling-rings { display: none; }
    .calling-overlay.is-active .calling-icon {
      background: rgba(34, 197, 94, 0.25);
      box-shadow: 0 0 24px rgba(34, 197, 94, 0.3);
    }
    @keyframes calling-pulse {
      0%   { transform: translate(-50%, -50%) scale(0.55); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(2.4);  opacity: 0; }
    }
    .calling-icon {
      position: relative; z-index: 1;
      width: 90px; height: 90px; border-radius: 50%;
      background: rgba(255,255,255,0.15);
      display: flex; align-items: center; justify-content: center;
      font-size: 40px;
      transition: background 0.3s, box-shadow 0.3s;
    }
    /* Action buttons row */
    .calling-actions {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 16px; padding: 0 4px; margin-bottom: 28px;
      flex-shrink: 0;
    }
    .cv-action {
      appearance: none; border: 0; padding: 0; background: transparent;
      color: #fff; cursor: pointer; font-family: inherit;
      display: flex; flex-direction: column; align-items: center; gap: 8px;
      transition: opacity 0.15s;
    }
    .cv-action:disabled { opacity: 0.4; cursor: not-allowed; }
    .cv-action-circle {
      width: 64px; height: 64px; border-radius: 50%;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.15);
      display: flex; align-items: center; justify-content: center;
      transition: background 0.15s, transform 0.1s, box-shadow 0.2s;
    }
    .cv-action:not(:disabled):hover .cv-action-circle {
      background: rgba(255,255,255,0.18);
    }
    .cv-action:not(:disabled):active .cv-action-circle {
      transform: scale(0.94);
    }
    .cv-action.is-active .cv-action-circle {
      background: #4a90e2;
      border-color: rgba(255,255,255,0.4);
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.25);
    }
    .cv-action-circle svg {
      width: 28px; height: 28px; fill: #fff;
    }
    .cv-action-label {
      font-size: 12px; color: #b8c5cd; font-weight: 500;
    }
    .cv-action.is-active .cv-action-label {
      color: #fff; font-weight: 600;
    }
    /* End-call button */
    .calling-end-wrap {
      display: flex; justify-content: center; flex-shrink: 0;
    }
    .calling-end {
      width: 68px; height: 68px; border-radius: 50%;
      background: #ef4444; border: 0; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 8px 24px rgba(239,68,68,.4);
      transition: background 0.15s, transform 0.1s;
    }
    .calling-end:hover { background: #dc2626; }
    .calling-end:active { transform: scale(0.94); }
    .calling-end svg {
      width: 30px; height: 30px; fill: #fff;
      transform: rotate(135deg);
    }
  `;
  document.head.appendChild(style);
}
